import { PureComponent, ReactNode, useEffect } from 'react';
import Select, {
    components,
    ControlProps,
    ValueContainerProps,
    OptionProps,
    MenuProps,
    StylesConfig,
    ValueType,
} from 'react-select';
import { GroupHeadingProps } from 'react-select/src/components/Group';
import Fuse from 'fuse.js';
import { CSSObject } from '@emotion/serialize';
import debounce from 'lodash/debounce';

import { fetchMatchingSymDia } from '../../../../../api';
import { SymDia, SymDiaResponse } from '../../../../../api/fetchMatchingSymDia';
import { LOCAL_ID_PREFIX } from '../../../../../constants';
import { GSymptomsDiagnosis } from '../../../../../store/db';
import { GlobalState } from '../../../../../store/reducers';
import {
    getDiagnosis,
    getSymptoms,
    getRelatedDiagnosis,
    getRelatedSymptoms,
    getPatientPersonalProfile,
} from '../../../../../store/selectors/prescriptionPad';
import {
    DiagnosisEntityOrSymptomsEntity,
    PatientPersonalProfile,
} from '../../../../../store/types/prescription';
import { connect } from 'react-redux';
import { RelatedSymDia } from '../../../../../api/fetchSymDiaFromSymDia';
import { ONE_CHAR_GENDER } from '../../../../../pages/NewPatient';
import { randomString } from '../../../../../store/utils';
import { PAD_V2_CONTAINER } from '../PadContainer';
import onContainerClick from '../../../../../pages/prescriptionPad/onClickContainer';
import { track } from '../../../../../tracking';
import {
    API_SEARCH,
    CUSTOM,
    GENIE,
    PxElSourceTrack,
    SUGGESTED,
    SYMPTOMS_KIND,
    DIAGNOSIS_KIND,
    LIST_LABEL_TYPE,
    AS_EMPTY,
    AS_OPTIONS_FREQUENCTLY,
    AS_OPTIONS_SUGGESTED,
    AS_SEARCH,
    AS_OPTIONS_SEARCH,
} from '../../../trackSource';
import { escTextIcon } from '../../../../common/Icons';
import { handleKeyDownEvent } from '../handleKeyDownEvent';
import {
    getGenieSetting,
    getIsICDSuggestionsEnabled,
} from '../../../../../store/selectors/session';
import PadGenieAutoSuggestFuseManager, {
    DIAGNOSIS,
    SYMPTOMS,
} from '../../../PadGenieAutoSuggestFuseManager';

export type SymDiaWithTracking = DiagnosisEntityOrSymptomsEntity & PxElSourceTrack;

export type SymDiaAutoSuggestItem = SymDia &
    PxElSourceTrack & { kind: typeof SYMPTOMS_KIND | typeof DIAGNOSIS_KIND };

type CompProps = {
    kind: typeof SYMPTOMS_KIND | typeof DIAGNOSIS_KIND;
    onSelect: (sym: SymDiaWithTracking) => void;
};

interface Props extends CompProps {
    elements: DiagnosisEntityOrSymptomsEntity[];
    patientProfile?: PatientPersonalProfile;
    relatedElements: RelatedSymDia[];
    isICDSuggestionsEnable?: boolean;
    disableAutoScrollOnSelect?: boolean;
}

const DropdownIndicator = null;

const optionHtml = (
    data: SymDiaAutoSuggestItem,
    isFocussed: boolean,
    isICDSuggestionsEnable?: boolean,
) => {
    const { id, name, kind, icd10_code = undefined, icd10_name = undefined } = data;

    return (
        <div
            key={id}
            id={id}
            className={`flex items-center justify-between cursor-pointer px-8 ${
                isFocussed ? 'focus-state-dropdown-item' : 'sy-dropdown-item'
            }`}
        >
            <div className="flex items-center py-4 space-x-8 ">
                <div className={kind === SYMPTOMS_KIND ? 'circle-sy' : 'circle-dx'}>
                    <p>{kind === SYMPTOMS_KIND ? 'Sx' : 'Dx'}</p>
                </div>
                {isICDSuggestionsEnable ? (
                    <p className="text-neutral-1 text-14">{`${name}${
                        icd10_code ? ` - ${icd10_code}` : ''
                    }`}</p>
                ) : (
                    <p className="text-neutral-1 text-14">{name}</p>
                )}
            </div>
            <div className="flex items-center space-x-12 movement-controls">
                <div className="flex items-center space-x-4">
                    <svg
                        className="fill-neutral-5 w-14 h-14"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                    >
                        <path d="M384 32H64C28.66 32 0 60.66 0 96v320c0 35.34 28.66 64 64 64h320c35.34 0 64-28.66 64-64V96C448 60.66 419.3 32 384 32zM350.6 254.6c-12.5 12.5-32.75 12.5-45.25 0L256 205.3V384c0 17.69-14.33 32-32 32s-32-14.31-32-32V205.3L142.6 254.6c-12.5 12.5-32.75 12.5-45.25 0s-12.5-32.75 0-45.25l103.1-103.1C209.3 97.4 219.1 96 224 96c4.881 0 14.65 1.391 22.65 9.398l103.1 103.1C363.1 221.9 363.1 242.1 350.6 254.6z"></path>
                    </svg>
                    <p className="text-neutral-1 text-12">Up</p>
                </div>
                <div className="flex items-center space-x-4">
                    <svg
                        className="w-14 h-14 fill-neutral-5"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                    >
                        <path d="M384 32H64C28.66 32 0 60.66 0 96v320c0 35.34 28.66 64 64 64h320c35.34 0 64-28.66 64-64V96C448 60.66 419.3 32 384 32zM350.6 302.6l-103.1 103.1C238.7 414.6 228.9 416 224 416c-4.881 0-14.65-1.391-22.65-9.398L97.38 302.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L192 306.8V128c0-17.69 14.33-32 32-32s32 14.31 32 32v178.8l49.38-49.38c12.5-12.5 32.75-12.5 45.25 0S363.1 290.1 350.6 302.6z"></path>
                    </svg>
                    <p className="text-neutral-1 text-12">Down</p>
                </div>
                <div className="flex items-center space-x-4">
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <rect
                            x="0.5"
                            y="0.5"
                            width="13"
                            height="13"
                            rx="1.5"
                            fill="#858C94"
                            stroke="#858C94"
                        ></rect>
                        <path
                            d="M2.90527 7.85927C8.8874 7.85927 10.803 7.85927 10.803 7.85927V3.57617"
                            stroke="#E5EDF6"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        ></path>
                        <path
                            d="M4.50024 5.5L1.77344 8.40059L4.50024 11"
                            stroke="#E5EDF6"
                            stroke-width="1.5"
                        ></path>
                    </svg>
                    <p className="text-neutral-1 text-12">Select</p>
                </div>
            </div>
        </div>
    );
};

const Option = (props: OptionProps<SymDiaAutoSuggestItem, true>) => {
    const { data, selectProps } = props;
    const { focussedItemId, setFocussedId, customProps } = selectProps;
    const { isICDSuggestionsEnable } = customProps;

    useEffect(() => {
        if (props.isFocused) {
            setFocussedId(data.id);
        }
    }, [props.isFocused]);

    const isFocussed = focussedItemId === data.id;

    return (
        <components.Option {...props} isFocused={isFocussed}>
            {optionHtml(data, isFocussed, isICDSuggestionsEnable)}
        </components.Option>
    );
};

export const SY_DY_V2_AS_INPUT = 'SY_DY_V2_AS_INPUT';

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = (props) => {
    const { innerRef, selectProps, onChange, onFocus, value } = props;
    const { placeholder, setMenuOnOff, customProps, kind } = selectProps;
    const { onBlur } = customProps;

    const onBBlur = (): void => {
        setMenuOnOff(false);
        onBlur();
    };

    const onFFocus = (): void => {
        setMenuOnOff(true);
        onFocus();
    };

    return (
        <div className="flex items-center justify-between w-full bg-transparent">
            <div className="flex items-center space-x-12 w-full">
                <svg
                    className="w-16 h-16 fill-darwin-neutral-400"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z"></path>
                </svg>
                <input
                    id={`${SY_DY_V2_AS_INPUT}-${kind}`}
                    ref={innerRef}
                    type="text"
                    className="w-full text-14 placeholder-darwin-neutral-400 text-neutral-black py-10 bg-transparent"
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onFocus={onFFocus}
                    onClick={onFFocus}
                    onBlur={onBBlur}
                    autoComplete="off"
                />
            </div>
        </div>
    );
};

const ValueContainer = (props: ValueContainerProps<SymDiaAutoSuggestItem, true>) => {
    const { children } = props;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const child = children[children.length - 1];

    return child;
};

const Control = (props: ControlProps<SymDiaAutoSuggestItem, true>) => {
    const { children } = props;
    return <components.Control {...props}>{children}</components.Control>;
};

const LoadingIndicator = null;

const Menu = (props: MenuProps<SymDiaAutoSuggestItem, true>) => {
    const { getValue, options } = props;

    const selectedValues = getValue();

    // const { inputValue } = props.selectProps;

    if (
        // (!inputValue && selectedValues.length === 0) ||
        // TODO check if this can be improved
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        (options[0]?.options?.length || 0) === 0 &&
        selectedValues.length === 0
    ) {
        return null;
    }

    const { children, ...otherProps } = props;

    return (
        <components.Menu {...otherProps}>
            <>{children}</>
        </components.Menu>
    );
};

const GroupHeading = (
    props: GroupHeadingProps<{ label: LIST_LABEL_TYPE; options: SymDiaAutoSuggestItem[] }, true>,
) => {
    const { children, selectProps } = props;
    const { options } = selectProps;
    const escIcon = escTextIcon('h-20 w-20');

    if (children === AS_OPTIONS_SEARCH || !options) {
        return null;
    }

    return (
        <div className="flex items-center justify-between px-8 pb-4">
            {children === AS_OPTIONS_FREQUENCTLY ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-bright-blue-800 px-6 py-4 caption-1 bold font-700 text-white rounded-4">
                        frequently
                    </div>
                    <p className="text-neutral-6 text-12 font-700">seen by you</p>
                </div>
            ) : children === AS_OPTIONS_SUGGESTED ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-sulphur-green-500 px-6 py-4 caption-1 bold text-white rounded-4">
                        associated
                    </div>
                    <p className="text-neutral-6 text-12 font-700">
                        to Symptoms / Diagnosis Entered
                    </p>
                </div>
            ) : (
                <div />
            )}
            {options && options.length > 0 && options[options.length - 1].label === children && (
                <div className="flex items-center space-x-4">
                    {escIcon}

                    <p className="text-neutral-1 body-3">Close</p>
                </div>
            )}
        </div>
    );
};
// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const MenuList = (props) => {
    return (
        <components.MenuList
            {...props}
            className="absolute flex z-10 w-full px-8 pt-12 overflow-auto bg-white top-40 shadow-tight-3 rounded-8 space-y-4 sy-dropdown"
        >
            {props.children}
        </components.MenuList>
    );
};
// TODO\
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const SelectContainer = (props) => (
    <components.SelectContainer {...props}>
        <div className="relative">{props.children}</div>
    </components.SelectContainer>
);

const NoOptionsMessage = () => null;
const LoadingMessage = () => null;

const customComponents = {
    DropdownIndicator,
    ValueContainer,
    Input,
    Control,
    Menu,
    MenuList,
    Option,
    LoadingIndicator,
    GroupHeading,
    NoOptionsMessage,
    LoadingMessage,
    SelectContainer,
};

const customStyles: StylesConfig<SymDiaAutoSuggestItem, true> = {
    option: (base) => {
        return {
            ...base,
            padding: 0,
        } as CSSObject;
    },
    menuList: () => {
        return {
            overflow: 'auto',
            maxHeight: 'calc(100vh - 180px)',
        } as CSSObject;
    },
    group: (base) => {
        return {
            ...base,
            paddingTop: 0,
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
        } as CSSObject;
    },
    container: () => {
        return { width: '100%' } as CSSObject;
    },
    menu: () => {
        return {} as CSSObject;
    },
    control: () => {
        return {} as CSSObject;
    },
};

type State = {
    isMenuOpen: boolean;
    selectedValues: SymDiaAutoSuggestItem[];
    inputValue: string;
    options: {
        label: LIST_LABEL_TYPE;
        options: SymDiaAutoSuggestItem[];
    }[];

    focussedItemId: string | undefined;
};

class SymDiaAutoSuggestV2 extends PureComponent<Props, State> {
    fetchDebounce: (input: string) => void;
    elId = `${this.props.kind}-suggest`;
    ref: Select<SymDiaAutoSuggestItem, true> | null = null;

    state: State = {
        isMenuOpen: false,
        selectedValues: [],
        inputValue: '',

        options: [],

        focussedItemId: undefined,
    };

    private readonly getOptionValue = (e: SymDiaAutoSuggestItem) => e.name;

    constructor(props: Props) {
        super(props);

        this.fetchDebounce = debounce(this.loadOptions, 300);
    }

    setOptionsWhenInputValueIsEmpty = (timeout: number): void => {
        const { kind, elements, relatedElements } = this.props;
        const type = kind === SYMPTOMS_KIND ? SYMPTOMS : DIAGNOSIS;
        const gElements = PadGenieAutoSuggestFuseManager.getOriginalListForType(type);

        if (!gElements) return;

        setTimeout(() => {
            const genieOptions: SymDiaAutoSuggestItem[] = gElements
                .filter((e) => !elements.find((t) => t.id === e.id))
                .map(
                    (s, i): SymDiaAutoSuggestItem => ({
                        ...s,
                        kind,
                        track: {
                            source: GENIE,
                            index: i,
                            label: AS_EMPTY,
                        },
                    }),
                );

            const relatedOptions: SymDiaAutoSuggestItem[] = relatedElements
                .filter((e) => {
                    if (elements.find((t) => t.id === e.id)) {
                        return false;
                    }
                    const genieIndex = genieOptions.findIndex((t) => t.id === e.id);
                    if (genieIndex > -1) {
                        genieOptions.unshift(genieOptions.splice(genieIndex, 1)[0]);
                        return false;
                    }

                    return true;
                })
                .map(
                    (s, i): SymDiaAutoSuggestItem => ({
                        ...s,
                        kind,

                        track: {
                            source: SUGGESTED,
                            index: i,
                            label: AS_EMPTY,
                        },
                    }),
                );

            genieOptions.splice(7);
            relatedOptions.splice(7);

            const options = [] as {
                label: LIST_LABEL_TYPE;
                options: SymDiaAutoSuggestItem[];
            }[];
            if (genieOptions.length > 0) {
                options.push({
                    label: AS_OPTIONS_FREQUENCTLY,
                    options: genieOptions.map((t) => ({
                        ...t,
                        track: { ...t.track, columnIndex: options.length },
                    })),
                });
            }
            if (relatedOptions.length > 0) {
                options.push({
                    label: AS_OPTIONS_SUGGESTED,
                    options: relatedOptions.map((t) => ({
                        ...t,
                        track: { ...t.track, columnIndex: options.length },
                    })),
                });
            }
            this.setState({
                options: options,
                focussedItemId:
                    genieOptions.length > 0
                        ? genieOptions[0].id
                        : relatedOptions.length > 0
                        ? relatedOptions[0].id
                        : undefined,
            });
        }, timeout);
    };

    componentDidUpdate = (prevProps: Props) => {
        if (prevProps.elements !== this.props.elements) {
            this.setOptionsWhenInputValueIsEmpty(0);
        }
    };

    onSelect = (options: ValueType<SymDiaAutoSuggestItem, true>): void => {
        this.setState({
            selectedValues: [],
        });

        if (!options || options.length !== 1) {
            return;
        }

        const option = options[0];

        const { onSelect } = this.props;

        const selectedObj = {
            id: option.id,
            name: option.name,
            icd10_code: option?.icd10_code,
            icd10_name: option?.icd10_name,
            track: option.track,
        };
        onSelect(selectedObj);

        track('PAD_AS_SELECT', {
            eid: option.id,
            searchString: this.state.inputValue,
            selectedString: option.name,
            kind: option.kind,
            label: option.track?.label,
            source: option.track?.source,
            index: option.track?.index?.toString(),
            total:
                (option.track?.columnIndex !== undefined &&
                    this.state.options[option.track?.columnIndex].options.length.toString()) ||
                undefined,
        });

        this.setState({
            selectedValues: [],
            isMenuOpen: false,
            inputValue: '',
            focussedItemId: undefined,
        });

        this.adjustScroll();

        this.ref?.blur();
    };

    onInputChange = (newValue: string): void => {
        this.setState({
            inputValue: newValue,
            isMenuOpen: true,
        });

        if (!newValue) {
            this.setOptionsWhenInputValueIsEmpty(0);
        } else {
            this.fetchDebounce(newValue);
        }
    };

    onInputFocus = (): void => {
        const { inputValue } = this.state;

        if (!inputValue) this.setOptionsWhenInputValueIsEmpty(0);

        this.adjustScroll();
    };

    loadOptions = (inputValue: string): void => {
        if (!inputValue || inputValue.length == 0) {
            return;
        }

        const { kind, elements } = this.props;
        const type = kind === SYMPTOMS_KIND ? 'symptoms' : 'diagnosis';
        const fuse = PadGenieAutoSuggestFuseManager.getFuseForType(type);

        if (!fuse) return;

        // search in genie
        const filteredGItems: SymDiaAutoSuggestItem[] = fuse
            .search(inputValue)
            .map((option) => ({ ...option.item }))
            .filter((e) => !elements.find((t) => t.id === e.id))
            .map((t, i) => ({
                ...t,
                kind,
                track: { source: GENIE, label: AS_SEARCH, index: i, columnIndex: 0 },
            }));

        filteredGItems.splice(7);

        this.setState({
            options: [{ label: AS_OPTIONS_SEARCH, options: filteredGItems }],
            focussedItemId: filteredGItems.length > 0 ? filteredGItems[0].id : undefined,
        });

        const { patientProfile } = this.props;

        fetchMatchingSymDia(
            inputValue,
            patientProfile?.gender
                ? ONE_CHAR_GENDER[patientProfile.gender.toLowerCase()]
                : undefined,
        )
            .then(async (response) => {
                this.setupSymDiaOptions(inputValue, response);
            })
            .catch(() => {
                this.setupSymDiaOptions(inputValue);
            });
    };

    setupSymDiaOptions = (inputValue: string, response?: SymDiaResponse): void => {
        if (this.state.inputValue !== inputValue) return;

        const { kind, elements } = this.props;
        const allOptions: SymDiaAutoSuggestItem[] = this.state?.options[0]?.options
            ? [...this.state.options[0].options]
            : [];

        (kind === SYMPTOMS_KIND ? response?.symptoms : response?.diagnoses)
            ?.filter((e) => !elements.find((t) => t.id === e.id))
            .forEach((i) => {
                if (!allOptions.find((s) => s.id === i.id)) {
                    allOptions.push({
                        ...i,
                        kind,

                        track: {
                            index: allOptions.length,
                            source: API_SEARCH,
                            label: AS_SEARCH,
                        },
                    });
                }
            });

        allOptions.splice(7);

        const isMatchFound = allOptions.find(
            (i) => i.name.toLowerCase() === inputValue.trim().toLowerCase(),
        );

        if (!isMatchFound && inputValue) {
            allOptions.push({
                id: `${LOCAL_ID_PREFIX}${
                    kind === SYMPTOMS_KIND ? 'sym' : 'dia'
                }-${new Date().getTime()}-${randomString(4)}`,
                name: inputValue,
                kind,

                track: {
                    index: allOptions.length,
                    source: CUSTOM,
                    label: AS_SEARCH,
                },
            });
        }

        track('AS_SEARCH', {
            searchString: this.state.inputValue,
            kind: kind === SYMPTOMS_KIND ? SYMPTOMS_KIND : DIAGNOSIS_KIND,
            total: allOptions?.map((option) => option.id)?.toString(),
        });

        this.setState({
            options: [{ label: AS_OPTIONS_SEARCH, options: allOptions }],
            focussedItemId: allOptions.length > 0 ? allOptions[0].id : undefined,
        });
    };

    adjustScroll = () => {
        if (this.props.disableAutoScrollOnSelect) {
            return;
        }

        const el = document.getElementById(this.elId) as HTMLDivElement;
        const con = document.getElementById(PAD_V2_CONTAINER) as HTMLDivElement;

        if (el && con) {
            const diff = 80 - (con.offsetHeight - (294 + el.getBoundingClientRect().y));
            diff > 0 &&
                con.scrollBy({
                    top: diff,
                    behavior: 'smooth',
                });
        }
    };

    private shouldShowMenu(newValue: string, selectedValuesLength: number): boolean {
        return (newValue && newValue.length > 0) || selectedValuesLength > 0 ? true : false;
    }

    private readonly filterOption = () => true;

    setMenuOnOff = (value: boolean): void => {
        this.setState({ isMenuOpen: value });
    };

    onBlur = (): void => {
        this.setState({
            selectedValues: [],
            isMenuOpen: false,
        });
        onContainerClick();
    };

    scrollIntoView = (id: string): void => {
        try {
            const myContainer = document.getElementById(id) as HTMLDivElement;
            if (myContainer) {
                myContainer.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        } catch (e) {
            console.error(e);
        }
    };

    setFocus = (newId: string | undefined) => {
        this.setState({
            focussedItemId: newId,
        });
    };

    onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
        if ((event.key === ' ' || event.keyCode === 32) && !this.state.inputValue) {
            event.preventDefault();
            return;
        }

        if (event.key === 'Escape') {
            this.ref?.blur();
            this.setState({
                isMenuOpen: false,
            });
            event.stopPropagation();
            event.preventDefault();
            return;
        }

        const { focussedItemId, options, isMenuOpen } = this.state;

        handleKeyDownEvent<SymDiaAutoSuggestItem>({
            event,
            focussedItemId,
            leftList: options.length > 0 ? options[0].options : [],
            rightList: options.length > 1 ? options[1].options : [],
            setFocus: this.setFocus,
            isMenuOpen,
            onSelect: this.onSelect,
            scrollIntoView: this.scrollIntoView,
        });
    };

    render(): ReactNode {
        const { kind } = this.props;
        const { inputValue, isMenuOpen, selectedValues, options, focussedItemId } = this.state;

        const placeholder =
            kind === SYMPTOMS_KIND
                ? 'Start typing Symptoms / Chief Complaints'
                : 'Start typing Diagnosis';

        return (
            <Select
                ref={(ref) => (this.ref = ref)}
                id={this.elId}
                value={selectedValues}
                isClearable={false}
                placeholder={placeholder}
                components={customComponents}
                styles={customStyles}
                filterOption={this.filterOption}
                getOptionValue={this.getOptionValue}
                options={options}
                onInputChange={this.onInputChange}
                inputValue={inputValue}
                onChange={this.onSelect}
                onFocus={this.onInputFocus}
                onKeyDown={this.onKeyDown}
                focussedItemId={focussedItemId}
                setFocussedId={this.setFocus}
                isMulti
                menuIsOpen={isMenuOpen}
                customProps={{
                    onBlur: this.onBlur,
                    isICDSuggestionsEnable: this.props?.isICDSuggestionsEnable,
                }}
                tabSelectsValue={false}
                setMenuOnOff={this.setMenuOnOff}
                menuPlacement="auto"
                kind={kind}
            />
        );
    }
}

export default connect((state: GlobalState, props: CompProps) => ({
    elements: (props.kind === SYMPTOMS_KIND ? getSymptoms : getDiagnosis)(state),
    patientProfile: getPatientPersonalProfile(state),
    relatedElements: (props.kind === SYMPTOMS_KIND ? getRelatedSymptoms : getRelatedDiagnosis)(
        state,
    ),
    isICDSuggestionsEnable: getIsICDSuggestionsEnabled(state),
    disableAutoScrollOnSelect: getGenieSetting(state, 'disableAutoScrollOnSelect') as boolean,
}))(SymDiaAutoSuggestV2);
